import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import useToken from '../common/helper/useToken';
import { getActiveToken } from '../common/helper/tokenStorage';
import { clearAllStorageAndState } from '../common/helper/storageCleanup';
import { lanePlannerEnabledForCompany } from '../common/constants';
import { getPageNameOverride } from '../constants/pageNameOverrides';
import { isMonitorPath } from '../constants/monitorPaths';
import { trackPageView } from '@/hooks';
import { useHelpLoginRedirect } from '@/layout/AppHelpCenter/hooks/useHelpLoginRedirect/useHelpLoginRedirect';
import { useSelector } from 'react-redux';

interface Permissions {
  is_view: boolean;
}

interface SubMenu {
  route: string;
  name?: string;
  permissions: Permissions;
}

export interface MenuItem {
  route: string;
  name?: string;
  permissions: Permissions;
  sub_menus: SubMenu[];
}

interface RootState {
  menu: {
    items: MenuItem[];
    status?: 'idle' | 'loading' | 'succeeded' | 'failed';
  };
}

/**
 * PrivateRoutes Component
 *
 * Route guard component that handles authentication and authorization.
 */

//Need to update menu logic from backend and Frontend -- TODO
const PrivateRoutes = () => {
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const menuStatus = useSelector((state: RootState) => state.menu.status);
  const { decodedUserToken } = useToken();
  const location = useLocation();
  const currentRoute =
    location.pathname.length > 1 && location.pathname.endsWith('/')
      ? location.pathname.replace(/\/+$/, '')
      : location.pathname;

  const firstAuthorizedRoute = (menuItems: MenuItem[]): string => {
    for (const item of menuItems) {
      if (item.route && item.permissions.is_view) {
        return item.route.startsWith('/') ? item.route : `/${item.route}`;
      }

      const authorizedSubMenu = item.sub_menus.find(
        (subMenu) => subMenu.route && subMenu.permissions.is_view
      );
      if (authorizedSubMenu) {
        return authorizedSubMenu.route.startsWith('/')
          ? authorizedSubMenu.route
          : `/${authorizedSubMenu.route}`;
      }
    }

    return '/login';
  };

  const token = getActiveToken();
  // A Help link opened while logged out reopens after login.
  useHelpLoginRedirect(token);

  // Resolves a route to the menu's own display label, so tracked page views
  // read as "Shipments" rather than a raw path. Routes outside the menu
  // (e.g. the /lane-planner pilot, gated by a company allowlist instead of
  // a menu row) fall through to getPageNameOverride below.
  const routeToPageName = useMemo(() => {
    const map = new Map<string, string>();
    const normalize = (route: string): string =>
      route.startsWith('/') ? route : `/${route}`;

    for (const item of main_menu ?? []) {
      if (item.route && item.name) {
        map.set(normalize(item.route), item.name);
      }
      for (const subMenu of item.sub_menus ?? []) {
        if (subMenu.route && subMenu.name) {
          map.set(normalize(subMenu.route), subMenu.name);
        }
      }
    }
    return map;
  }, [main_menu]);

  // Audits page views for the user activity feature (ZD-2805). Placed here,
  // not in DashboardLayout, because this is the one ancestor common to every
  // private route -- including ones that don't render DashboardLayout at all
  // (onboarding, the /shipment/external-view map). Fires on the route change
  // itself, before the permission check below decides whether to redirect,
  // so a denied attempt is still recorded -- consistent with auditing what
  // the user tried to do, not only what they ended up seeing.
  useEffect(() => {
    if (token) {
      const pageName =
        routeToPageName.get(currentRoute) ?? getPageNameOverride(currentRoute);
      trackPageView(currentRoute, pageName);
    }
  }, [currentRoute, token, routeToPageName]);

  if (!token) {
    return <Navigate to='/login' />;
  } else if (currentRoute === '/confirmation') {
    return <Outlet />;
  } else {
    // Lane planner (cargo.one) pilot: gated by company allowlist rather than
    // a Features menu row. Check before the empty-menu logout branch so a
    // briefly-empty Redux menu cannot bounce allowlisted users to /login.
    if (currentRoute === '/lane-planner') {
      const decodedToken = decodedUserToken();
      if (lanePlannerEnabledForCompany(decodedToken?.company_id)) {
        return <Outlet />;
      }
      if (main_menu?.length > 0) {
        return <Navigate to={firstAuthorizedRoute(main_menu)} replace />;
      }
      if (menuStatus === 'loading' || menuStatus === 'idle') {
        return null;
      }
      const _ = clearAllStorageAndState({ broadcast: true });
      return <Navigate to='/login' replace />;
    }

    if (main_menu?.length > 0) {
      let check = main_menu.findIndex((x) => x.route === 'airport-list');
      if (
        check > -1 &&
        (currentRoute.includes('/airport-list/') ||
          currentRoute.includes('/airline-detail/'))
      ) {
        return <Outlet />;
      }

      check = main_menu.findIndex((x) => x.route === 'shipment');
      if (check > -1 && currentRoute.includes('/shipment/external-view/')) {
        return <Outlet />;
      }
      check = main_menu.findIndex((x) => x.route === 'settings');
      const subRoute = main_menu[check]?.sub_menus.findIndex(
        (x) => x.route === '/settings/gxp-reports'
      );
      if (subRoute > -1 && currentRoute.includes('/publish-gxp-report/')) {
        return <Outlet />;
      }

      check = main_menu.findIndex((x) => x.route === 'settings');
      if (check > -1) {
        const subRoute = main_menu[check].sub_menus.findIndex(
          (x) => x.route === '/settings/gxp-reports'
        );
        if (subRoute > -1 && currentRoute.includes('/publish-gxp-report/')) {
          return <Outlet />;
        }
      }

      if (
        import.meta.env.DEV &&
        currentRoute === '/dev/quickstat-booking-complete'
      ) {
        return <Outlet />;
      }

      if (
        currentRoute === '/onboarding' ||
        currentRoute === '/create-account'
      ) {
        const decodedToken = decodedUserToken();
        const role = decodedToken?.role?.toLowerCase() === 'admin';
        if (role === true) {
          return <Outlet />;
        }

        return <Navigate to={firstAuthorizedRoute(main_menu)} replace />;
      }

      const hasPermission = (menuItems: MenuItem[], route: string): boolean => {
        for (const item of menuItems) {
          if (`/${item.route}` === route) {
            return item.permissions.is_view;
          }
          const subMenuItem = item.sub_menus.find((subMenu) => {
            // Normalize routes by ensuring they start with /
            const normalizedSubRoute = subMenu.route.startsWith('/')
              ? subMenu.route
              : `/${subMenu.route}`;
            if (normalizedSubRoute === route) {
              return subMenu;
            }
            return null;
          });

          if (subMenuItem) {
            return subMenuItem.permissions.is_view;
          }
        }
        return false;
      };

      // The TV monitor is a read-only view of the dashboard map, so it
      // follows the dashboard's permission rather than its own menu row.
      if (isMonitorPath(currentRoute)) {
        return hasPermission(main_menu, '/dashboard') ? (
          <Outlet />
        ) : (
          <Navigate to={firstAuthorizedRoute(main_menu)} replace />
        );
      }

      const permissionGranted = hasPermission(main_menu, currentRoute);
      if (!permissionGranted) {
        return <Navigate to={firstAuthorizedRoute(main_menu)} replace />;
      }
      return <Outlet />;
    } else if (menuStatus === 'loading' || menuStatus === 'idle') {
      // Menu is still loading in the background (e.g. initial login or session switch) - do not logout
      return null;
    } else {
      const _ = clearAllStorageAndState({ broadcast: true });
      return <Navigate to='/login' replace />;
    }
  }
};

export default PrivateRoutes;
